import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Sun, Moon, LogOut, Wifi, WifiOff, RotateCw, RefreshCw } from 'lucide-react';
import { LogoMark } from './Logo';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/widgets/Pill';
import { SessionStateBadge } from '@/components/widgets/SessionStateBadge';
import { SessionCountdown } from '@/components/widgets/SessionCountdown';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUiStore } from '@/stores/ui';
import { useAuthStore } from '@/stores/auth';
import { useSystemStore } from '@/stores/system';
import { useWsStatus } from '@/realtime/use-channel';
import { nowBeijing, nowBeijingDate } from '@/lib/format';
import { ForceLiquidateButton } from '@/features/force-liquidate/ForceLiquidateButton';
import { GlobalDetachedBadge } from '@/features/detach';
import { AlertsBell } from './AlertsBell';

export function TopBar() {
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const admin = useAuthStore((s) => s.admin);
  const clearAuth = useAuthStore((s) => s.clear);
  const sessionState = useSystemStore((s) => s.sessionState);
  const wsStatus = useWsStatus();
  const qc = useQueryClient();

  const [clock, setClock] = useState(() => nowBeijing());
  const [date] = useState(() => nowBeijingDate());
  useEffect(() => {
    const id = setInterval(() => setClock(nowBeijing()), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <header className="flex h-14 items-center justify-between gap-4 border-b border-line bg-bg-1 px-4">
      <div className="flex items-center gap-3">
        <LogoMark />
        <SessionStateBadge state={sessionState} />
        {/* 常驻脱钩徽标:未脱钩不占位。脱钩后系统仍在跑但真实/虚拟口径已断,不常驻则切页即不可见 */}
        <GlobalDetachedBadge />
        <SessionCountdown />
      </div>

      <div className="flex items-center gap-3">
        <span className="hidden md:inline text-xs text-ink-2 num">
          北京 <span className="text-ink-3">{date}</span> {clock}
        </span>

        <Pill tone={wsStatus === 'open' ? 'up' : wsStatus === 'idle' ? 'neutral' : 'warn'}>
          {wsStatus === 'open' ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
          <span className="hidden sm:inline">
            {wsStatus === 'open' ? '已连接' : wsStatus === 'reconnecting' ? '重连中' : wsStatus === 'connecting' ? '连接中' : '未连接'}
          </span>
        </Pill>

        <ForceLiquidateButton variant="compact" />

        <AlertsBell />

        <Button
          variant="ghost"
          size="icon"
          onClick={() => qc.invalidateQueries({ queryKey: ['admin'] })}
          aria-label="全局刷新"
          title="刷新所有数据 · 全局"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>

        <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="切换主题">
          {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <span className="text-xs">{admin?.displayName || admin?.username || 'admin'}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel>{admin?.username || '未登录'}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <RotateCw className="mr-2 h-4 w-4" />
              修改密码
            </DropdownMenuItem>
            <DropdownMenuItem onClick={clearAuth} className="text-down">
              <LogOut className="mr-2 h-4 w-4" />
              退出登录
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
